import {$isMarkNode} from '@lexical/mark';
import {DocumentThread} from 'client';
import {$isHighlightNode} from 'components/DSYS/editor/nodes/HighlightNode';
import {$isRedactionNode} from 'components/DSYS/editor/nodes/RedactionNode';
import {TOGGLE_REDACTION_EDITOR_COMMAND} from 'components/DSYS/editor/plugins/FloatingRedactionEditor';
import {getPlanningCommentOriginIdFromElement} from 'components/DSYS/editor/utils/planningCommentOrigin';
import {LexicalEditor, LexicalNode} from 'lexical';
import {$isAISuggestionMarkNode} from 'plugins/aiRules/AISuggestionMarkNode';

/**
 * Click handler for special node types in the editor.
 */
export type NodeClickHandler = {
  name?: string;
  matcher: (node: LexicalNode) => boolean;
  onNodeClick: (node: LexicalNode, editor: LexicalEditor) => boolean;
};

type ClickHandlerFactoryContext = {
  setActiveAISuggestion: (val: any) => void;
  setActiveComment: (val: any) => boolean;
  threads?: DocumentThread[];
  disableAISuggestions?: boolean;
};

const getFirstActiveThreadId = (
  threadIds: readonly string[],
  threads: readonly DocumentThread[] = []
): string | null => {
  const allowed = new Set(threadIds);
  const firstActive = threads.find(
    (t) => allowed.has(t.id) && !t.is_resolved && t.comments?.some((c) => !c.is_deleted)
  );
  return firstActive?.id ?? null;
};

/**
 * Creates the default click handlers for the editor.
 * Handlers are executed in the order they appear in the array.
 */
export const createDefaultClickHandlers = ({
  setActiveAISuggestion,
  setActiveComment,
  threads,
  disableAISuggestions = false,
}: ClickHandlerFactoryContext): NodeClickHandler[] => {
  const handlers: NodeClickHandler[] = [
    // Redaction handler
    {
      name: 'redaction',
      matcher: $isRedactionNode,
      onNodeClick: (node, editor) => {
        if (!$isRedactionNode(node) || !node.isCurrentUserOwner()) {
          return false;
        }

        // Ensure only one special toolbar is open at a time
        if (!setActiveComment(null)) {
          return false;
        }
        setActiveAISuggestion(null);

        const nodeKey = node.getKey();
        editor.dispatchCommand(TOGGLE_REDACTION_EDITOR_COMMAND, {nodeKey});
        return true;
      },
    },

    // Comment handler - handles HighlightNode
    {
      name: 'comment',
      // Also match MarkNode subclasses so a click on an AI suggestion, placeholder or
      // word-count mark inside a comment stops here and falls through to its own handler
      // instead of opening the surrounding comment.
      matcher: (node) => $isMarkNode(node) || $isHighlightNode(node),
      onNodeClick: (node, editor) => {
        if (!$isHighlightNode(node)) return false;

        const threadIds = node.getHighlightIds();
        if (threadIds.length === 0) return false;

        // If multiple thread IDs, use the first active one (typically the most specific/inner comment)
        // Since IDs are added in order when overlaps are created, the first is usually most specific
        const selectedThreadId = getFirstActiveThreadId(threadIds, threads);
        if (!selectedThreadId) return false;

        const thread = threads?.find((t) => t.id === selectedThreadId);
        const editorKey = editor.getKey();
        const nodeElement = editor.getElementByKey(node.getKey());
        const planningCommentOriginId =
          getPlanningCommentOriginIdFromElement(nodeElement) ?? undefined;

        if (thread) {
          const hasActiveComments = thread.comments?.some((comment) => !comment.is_deleted);

          // Only open if thread has active comments
          if (hasActiveComments) {
            if (
              !setActiveComment({threadId: selectedThreadId, editorKey, planningCommentOriginId})
            ) {
              return false;
            }
            setActiveAISuggestion(null);
            return true;
          }
          return false;
        } else {
          // Thread not found - might still be loading
          if (!setActiveComment({threadId: selectedThreadId, editorKey, planningCommentOriginId})) {
            return false;
          }
          setActiveAISuggestion(null);
          return true;
        }
      },
    },
  ];

  if (!disableAISuggestions) {
    handlers.push({
      name: 'ai-suggestion',
      matcher: $isAISuggestionMarkNode,
      onNodeClick: (node, editor) => {
        if (!$isAISuggestionMarkNode(node)) return false;

        // Skip if this suggestion should defer to smaller suggestions that should take precedence
        const nodeKey = node.getKey();
        const domElement = editor.getElementByKey(nodeKey);
        if (domElement && node.shouldDeferToOtherSuggestionDOM) {
          const suggestionId = node.getSuggestionId();
          if (node.shouldDeferToOtherSuggestionDOM(domElement as HTMLElement, suggestionId)) {
            return false; // Let the smaller suggestion handle the click
          }
        }

        const suggestionData = node.getSuggestionData();

        // Don't open rejected suggestions or suggestions without a header
        if (suggestionData.isRejected || !suggestionData.headerId) {
          return false;
        }

        if (domElement) {
          if (!setActiveComment(null)) {
            return false;
          }
          setActiveAISuggestion({
            suggestion: {
              id: node.getSuggestionId(),
              originalText: node.getTextContent(),
              suggestedText: suggestionData.suggestedText || '',
              confidence: suggestionData.confidence || 0.8,
              ruleId: suggestionData.ruleId || 'unknown',
              isRejected: false,
            },
            headingId: suggestionData.headerId,
            node: domElement as HTMLElement,
          });
          return true;
        }

        return false;
      },
    });
  }

  return handlers;
};
